/* Teacher availability — READ-ONLY view with the approval workflow.
   Teachers can submit requests to ADD / CHANGE / REMOVE availability.
   Admin/Super Admin/Owner must approve before the change goes live. */
import { useState } from "react";
import api, { useList, errMsg, DAYS } from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Clock, Plus, Pencil, Trash2, ShieldCheck, Hourglass, AlertCircle, Info,
} from "lucide-react";

const STATUS_STYLE = {
  approved: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Approved · bookable" },
  pending: { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "Pending admin approval" },
  amendment_requested: { cls: "bg-orange-100 text-orange-800 border-orange-200", label: "Amendment requested" },
  rejected: { cls: "bg-rose-100 text-rose-800 border-rose-200", label: "Rejected" },
  inactive: { cls: "bg-zinc-100 text-zinc-700 border-zinc-200", label: "Removed" },
  superseded: { cls: "bg-zinc-100 text-zinc-700 border-zinc-200", label: "Superseded" },
  draft: { cls: "bg-zinc-100 text-zinc-700 border-zinc-200", label: "Draft" },
};

const REQUEST_LABEL = {
  new_teacher: "New teacher availability",
  add_time: "Add teaching time",
  change_time: "Change teaching time",
  remove_time: "Remove teaching time",
};

function StatusPill({ status, testId }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span data-testid={testId}
      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.cls}`}>
      {s.label}
    </span>
  );
}

function RequestDialog({ open, onOpenChange, mode, prefill, onSubmitted }) {
  // mode: "add" | "change" | "remove"
  const [day, setDay] = useState(prefill?.day_of_week ?? 1);
  const [start, setStart] = useState(prefill?.start_time || "18:00");
  const [end, setEnd] = useState(prefill?.end_time || "20:00");
  const [instrument, setInstrument] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const title = mode === "add" ? "Request to add teaching time"
              : mode === "change" ? "Request to change a teaching slot"
              : "Request to remove a teaching slot";

  const reqType = mode === "add" ? "add_time"
                : mode === "change" ? "change_time"
                : "remove_time";

  const submit = async () => {
    if (mode !== "remove" && start >= end) {
      toast.error("End time must be after start time"); return;
    }
    setBusy(true);
    try {
      const payload = {
        day_of_week: Number(day),
        start_time: start,
        end_time: end,
        request_type: reqType,
        target_window_id: prefill?.id || undefined,
        instrument: instrument || undefined,
        effective_from: effectiveFrom || undefined,
        note: note || undefined,
      };
      await api.post("/teacher/availability-requests", payload);
      toast.success("Thank you for submitting. Admin will review and approve before it becomes available for student booking.");
      onOpenChange(false);
      onSubmitted && onSubmitted();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="availability-request-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {mode === "remove"
              ? "This will remove the slot from your official availability once admin approves. Admin will check for any affected students before approving."
              : mode === "change"
              ? "This will replace the existing slot once admin approves. Admin will check for any affected students before approving."
              : "Tell us which day & time you'd like to add. Admin will review and approve before it becomes bookable."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Day</Label>
            <select value={day} onChange={(e) => setDay(e.target.value)}
                    data-testid="req-day"
                    disabled={mode === "remove"}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-card disabled:bg-muted/50">
              {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Start</Label>
            <Input type="time" value={start} onChange={(e) => setStart(e.target.value)}
                   disabled={mode === "remove"} data-testid="req-start" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">End</Label>
            <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)}
                   disabled={mode === "remove"} data-testid="req-end" />
          </div>
        </div>
        {mode !== "remove" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Instrument / course (optional)</Label>
              <Input value={instrument} onChange={(e) => setInstrument(e.target.value)}
                     placeholder="e.g. Piano" data-testid="req-instrument" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Effective from (optional)</Label>
              <Input type="date" value={effectiveFrom}
                     onChange={(e) => setEffectiveFrom(e.target.value)} data-testid="req-effective-from" />
            </div>
          </div>
        )}
        <div className="space-y-1.5 mt-2">
          <Label className="text-xs">Note to admin (optional)</Label>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    data-testid="req-note" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="req-submit">
            {busy ? "Submitting..." : "Submit for approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TeacherAvailabilityPage() {
  const { data, loading, reload } = useList("/teacher/availability/all");
  const [dialog, setDialog] = useState({ open: false, mode: "add", prefill: null });

  if (loading || !data) return <Spinner full />;
  const approved = data.approved || [];
  const pending = (data.pending_requests || []).filter(
    (r) => r.status === "pending" || r.status === "amendment_requested");

  // Group approved by day
  const byDay = DAYS.map((_, d) =>
    approved.filter((w) => w.day_of_week === d && w.is_available !== false)
      .sort((a, b) => a.start_time.localeCompare(b.start_time)));

  return (
    <div data-testid="teacher-availability-page">
      <PageHeader title="My availability"
        description="Your availability is reviewed by school admin. Changes go live only after approval."
        actions={
          <Button onClick={() => setDialog({ open: true, mode: "add", prefill: null })}
                  data-testid="request-add-time-btn">
            <Plus className="h-4 w-4 mr-1" />Request to add time
          </Button>
        } />

      {/* Info banner */}
      <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50/60 p-4 flex gap-3 text-xs"
           data-testid="approval-info-banner">
        <Info className="h-4 w-4 text-sky-700 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-sky-900 text-sm">Approval workflow</p>
          <p className="text-sky-800/80 mt-1">
            To open more teaching time, request a change, or remove a slot, submit a request.
            Admin / Super Admin / Owner will review (we check for affected students) and approve
            before it becomes bookable.
          </p>
        </div>
      </div>

      {/* Pending requests */}
      {pending.length > 0 && (
        <div className="mb-6 rounded-lg border bg-card p-5" data-testid="pending-requests-section">
          <div className="flex items-center gap-2 mb-3">
            <Hourglass className="h-4 w-4 text-amber-600" />
            <h3 className="text-base font-semibold">Pending admin approval</h3>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                  data-testid="pending-count">
              {pending.length}
            </span>
          </div>
          <div className="space-y-2">
            {pending.map((r) => (
              <div key={r.id} className="rounded-md border bg-amber-50/30 px-3 py-2 text-sm"
                   data-testid={`pending-req-${r.id}`}>
                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <div>
                    <p className="font-medium">
                      {REQUEST_LABEL[r.request_type] || "Availability change"} · {DAYS[r.day_of_week]} {r.start_time}–{r.end_time}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {r.instrument && `Instrument: ${r.instrument} · `}
                      {r.effective_from && `From: ${r.effective_from} · `}
                      Submitted {new Date(r.created_at).toLocaleString()}
                    </p>
                    {r.note && <p className="text-xs italic text-muted-foreground mt-1">&quot;{r.note}&quot;</p>}
                    {r.admin_note && r.status === "amendment_requested" && (
                      <p className="text-xs text-orange-800 mt-1">
                        <AlertCircle className="h-3 w-3 inline mr-1" />
                        Admin wants changes: {r.admin_note}
                      </p>
                    )}
                  </div>
                  <StatusPill status={r.status} testId={`pending-status-${r.id}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approved availability */}
      <div className="space-y-3 max-w-3xl">
        {DAYS.map((name, d) => (
          <div key={name}
               className={`rounded-lg border p-4 ${byDay[d].length ? "bg-card" : "bg-muted/30"}`}
               data-testid={`availability-day-${d}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Clock className={`h-4 w-4 ${byDay[d].length ? "text-secondary" : "text-muted-foreground"}`} />
                <p className="text-sm font-medium">{name}</p>
                <span className="text-xs text-muted-foreground">
                  {byDay[d].length === 0 ? "No availability" : `${byDay[d].length} slot${byDay[d].length > 1 ? "s" : ""}`}
                </span>
              </div>
            </div>
            {byDay[d].length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                Want to teach on {name}? Use &quot;Request to add time&quot; above.
              </p>
            ) : (
              byDay[d].map((w) => (
                <div key={w.id}
                     className="flex flex-wrap items-center gap-2 py-2 border-t first:border-t-0"
                     data-testid={`availability-slot-${w.id}`}>
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  <span className="font-mono text-sm w-28">{w.start_time}–{w.end_time}</span>
                  <span className="text-xs text-muted-foreground flex-1">
                    {w.studio_name ? `Preferred: ${w.studio_name}` : "Any studio"}
                  </span>
                  <StatusPill status="approved" testId={`status-${w.id}`} />
                  <Button size="sm" variant="ghost"
                          onClick={() => setDialog({ open: true, mode: "change", prefill: w })}
                          data-testid={`request-change-${w.id}`}>
                    <Pencil className="h-3 w-3 mr-1" /> Change
                  </Button>
                  <Button size="sm" variant="ghost"
                          onClick={() => setDialog({ open: true, mode: "remove", prefill: w })}
                          data-testid={`request-remove-${w.id}`}>
                    <Trash2 className="h-3 w-3 mr-1 text-rose-600" /> Remove
                  </Button>
                </div>
              ))
            )}
          </div>
        ))}
        {approved.length === 0 && pending.length === 0 && (
          <EmptyState text="No availability yet. Click 'Request to add time' to propose your first slot." />
        )}
      </div>

      <RequestDialog open={dialog.open} mode={dialog.mode} prefill={dialog.prefill}
                     onOpenChange={(o) => setDialog({ ...dialog, open: o })}
                     onSubmitted={reload} />
    </div>
  );
}
